import { Switch } from '@odekoteam/doppio';
import { useEffect, useState } from 'react';
import { get } from 'react-native/Libraries/TurboModule/TurboModuleRegistry';
import { UPDATE_LOCATIONS_DELIVER_WITHIN_STORE_HOURS } from 'src/graphql/requests/updateLocationsDeliverWithinStoreHours';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { routingLocationsSelector } from 'src/state/selectors/locations';
import { updateRoutingLocations } from 'src/state/slices/locations';
import { getScheduledDeliveryTimeSource } from 'src/utils/locations';
import { Flex } from 'theme-ui';
import { useMutation } from 'urql';

const DeliverWithinStoreHoursSwitch = ({
  locationId,
  value,
  storeHoursConfirmed = false,
}: {
  locationId: string;
  value?: boolean;
  storeHoursConfirmed?: boolean;
}): JSX.Element => {
  const routingLocations = useAppSelector(routingLocationsSelector);
  const dispatch = useAppDispatch();
  const locationInState = routingLocations.find(
    (location) => location.locationId === locationId,
  );
  const [isChecked, setIsChecked] = useState(
    locationInState ? locationInState.deliverWithinStoreHours : value,
  );
  const [isDisabled, setIsDisabled] = useState(false);
  const [
    updateLocationsDeliverWithinStoreHoursResult,
    updateLocationsDeliverWithinStoreHours,
  ] = useMutation(UPDATE_LOCATIONS_DELIVER_WITHIN_STORE_HOURS);

  const handleSwitch = (newValue: boolean): void => {
    if (!isDisabled) {
      updateLocationsDeliverWithinStoreHours({
        input: {
          locationIds: [locationId],
          deliverWithinStoreHours: newValue,
        },
      });
      // optimistic load
      setIsChecked(newValue);
      dispatch(
        updateRoutingLocations([
          {
            locationId,
            deliverWithinStoreHours: newValue,
            scheduledDeliveryTimeSource: getScheduledDeliveryTimeSource(
              newValue,
              storeHoursConfirmed,
            ),
          },
        ]),
      );
      // debounce
      setIsDisabled(true);
    }
  };

  useEffect(() => {
    const updatedLocations =
      updateLocationsDeliverWithinStoreHoursResult?.data
        ?.updateLocationsDeliverWithinStoreHours?.updatedLocations;
    if (updatedLocations && updatedLocations.length > 0) {
      // if the mutation succeeds - check the deliverWithinStoreHours
      setIsChecked(updatedLocations[0].deliverWithinStoreHours);
      dispatch(
        updateRoutingLocations([
          {
            locationId,
            deliverWithinStoreHours:
              updatedLocations[0].deliverWithinStoreHours,
            scheduledDeliveryTimeSource:
              updatedLocations[0].scheduledDeliveryTimeSource,
          },
        ]),
      );
      setIsDisabled(false);
    }
  }, [updateLocationsDeliverWithinStoreHoursResult, dispatch, locationId]);

  const getLabel = (): string => {
    if (isChecked) return 'On';
    else return 'Off';
  };
  return (
    <Flex>
      <Switch
        id={`deliver-within-store-hours-switch-${locationId}`}
        accessibilityLabel="deliver within store hours toggle"
        onCheckedChange={handleSwitch}
        size="default"
        isChecked={isChecked}
      />
      <span sx={{ marginLeft: '12px', variant: 'text.paragraphLgDefault' }}>
        {getLabel()}
      </span>
    </Flex>
  );
};

export default DeliverWithinStoreHoursSwitch;
